<template>
    <nav
        v-if="pagination && (pagination.total ?? 0) > 0"
        class="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700"
        :aria-label="t('common.page')"
    >
        <p class="text-sm text-gray-700 dark:text-gray-300">
            {{ t("common.showing") }}
            <span class="font-medium">{{ pagination.from ?? 0 }}</span>
            {{ t("common.to") }}
            <span class="font-medium">{{ pagination.to ?? 0 }}</span>
            {{ t("common.of") }}
            <span class="font-medium">{{ pagination.total ?? 0 }}</span>
            {{ t("common.results") }}
        </p>

        <div class="flex flex-wrap items-center justify-center gap-3">
            <div v-if="perPage !== undefined" class="flex items-center gap-2">
                <label
                    class="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap"
                >
                    {{ t("pagination.rowsPerPage") }}
                </label>
                <select
                    :value="perPage"
                    :disabled="disabled"
                    class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                    @change="
                        emit(
                            'perPageChange',
                            Number(($event.target as HTMLSelectElement).value),
                        )
                    "
                >
                    <option
                        v-for="option in perPageOptions"
                        :key="option"
                        :value="option"
                    >
                        {{ option }}
                    </option>
                </select>
            </div>

            <div class="flex flex-wrap items-center justify-center gap-1">
                <button
                    type="button"
                    class="min-h-[36px] px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    :disabled="currentPage <= 1 || disabled"
                    :aria-label="t('common.previous')"
                    @click="goToPage(currentPage - 1)"
                >
                    {{ t("common.previous") }}
                </button>

                <template v-for="(page, index) in visiblePages" :key="index">
                    <span
                        v-if="page === '...'"
                        class="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400"
                    >
                        …
                    </span>
                    <button
                        v-else
                        type="button"
                        class="min-h-[36px] min-w-[36px] px-3 py-1.5 rounded-md border text-sm font-medium transition-colors"
                        :class="[
                            page === currentPage
                                ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-600 dark:text-primary-300'
                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700',
                        ]"
                        :disabled="disabled"
                        :aria-current="
                            page === currentPage ? 'page' : undefined
                        "
                        @click="goToPage(page as number)"
                    >
                        {{ page }}
                    </button>
                </template>

                <button
                    type="button"
                    class="min-h-[36px] px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    :disabled="currentPage >= lastPage || disabled"
                    :aria-label="t('common.next')"
                    @click="goToPage(currentPage + 1)"
                >
                    {{ t("common.next") }}
                </button>
            </div>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface PaginationMeta {
    current_page?: number;
    last_page?: number;
    from?: number | null;
    to?: number | null;
    total?: number;
}

interface Props {
    pagination: PaginationMeta | null;
    disabled?: boolean;
    perPage?: number;
    perPageOptions?: number[];
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    perPage: undefined,
    perPageOptions: () => [10, 50, 100, 250, 500, 1000],
});

const emit = defineEmits<{
    pageChange: [page: number];
    perPageChange: [value: number];
}>();

const { t } = useI18n();

const currentPage = computed(() => props.pagination?.current_page ?? 1);
const lastPage = computed(() => props.pagination?.last_page ?? 1);

const visiblePages = computed<(number | "...")[]>(() => {
    const current = currentPage.value;
    const last = lastPage.value;
    const delta = 2;

    if (last <= 7) {
        return Array.from({ length: last }, (_, i) => i + 1);
    }

    const range: number[] = [];
    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    const pages: (number | "...")[] = [];
    pages.push(1);
    if (current - delta > 2) {
        pages.push("...");
    }
    pages.push(...range);
    if (current + delta < last - 1) {
        pages.push("...");
    }
    if (last > 1) {
        pages.push(last);
    }
    return pages;
});

const goToPage = (page: number) => {
    if (page < 1 || page > lastPage.value || page === currentPage.value) {
        return;
    }
    emit("pageChange", page);
};
</script>
